import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useGetKnowledgeBaseArticleBySlugQuery } from "knowledge-base/_api/knowledge-base-api";
import ArticleEditor from "knowledge-base/articles/_views/article-editor";

type Props = Readonly<{
  slug: string;
  /** Whose knowledge base this article is being edited in — see ArticleBySlugArgs. */
  ownerLegalEntityId?: string;
}>;

/**
 * Loads the article the editor edits.
 *
 * Split from the editor itself so the editor can take a loaded article and hold its draft state
 * without also handling the loading and failure cases — a form whose fields are initialised from a
 * value that may not have arrived yet is where stale-draft bugs come from.
 */
export default function ArticleEditorPane({ slug, ownerLegalEntityId }: Props) {
  const {
    data: article,
    isLoading,
    isError,
  } = useGetKnowledgeBaseArticleBySlugQuery({ slug, ownerLegalEntityId });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !article) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        This page does not exist, or it is not one you can edit.
      </Alert>
    );
  }

  // The editor's draft state is seeded from the article, so it has to be rebuilt when a different
  // article is opened. Keying on the id is what guarantees that.
  return <ArticleEditor article={article} key={article.id} />;
}
